// i18n bundle for the ENGAGE module
export const ENGAGE_TRANSLATIONS = {
    en: {
        engage: {
            tabs: { campaigns: "Campaigns", templates: "Templates", inbox: "Inbox" },
            campaigns: {
                title: "Campaigns",
                subtitle: "Multi-step sequences with AI-generated emails, A/B testing and unified inbox.",
                empty: "No campaigns yet",
                emptyBody: "Create your first outbound sequence — GPT-5.2 will draft the emails for you.",
                create: "New campaign",
                statusLabels: { draft: "Draft", active: "Active", paused: "Paused", completed: "Completed" },
                cols: { name: "Campaign", leads: "Leads", sent: "Sent", open: "Open %", reply: "Reply %", status: "Status", updated: "Updated" },
                pause: "Pause",
                resume: "Resume",
                delete: "Delete",
                view: "View",
                sending: "Sending…",
                sentBanner: (n, sim) => sim ? `Scheduled ${n} emails (simulation mode — add RESEND_API_KEY to send for real)` : `Sent ${n} emails via Resend`,
            },
            wizard: {
                title: "Create campaign",
                steps: { audience: "Audience", sequence: "Sequence", review: "Review & send" },
                back: "Back",
                next: "Next",
                cancel: "Cancel",
                nameLabel: "Campaign name",
                namePh: "e.g. EU Fintech Q3 outreach",
                senderLabel: "Sender name (as it appears in the email)",
                senderPh: "e.g. Alex from BOXEB2B",
                valueLabel: "Value proposition (one sentence, used by the AI)",
                valuePh: "e.g. Self-serve alternative to $40k lead-gen agencies — 50 free credits.",
                audienceSource: "Audience source",
                sourceSegment: "From a saved segment",
                sourceCurrent: "From current leads table filters",
                sourceManual: "Pick leads manually",
                selectSegment: "Select a segment",
                noSegments: "No segments yet — build one in FIND → Segments.",
                leadsSelected: (n) => `${n} leads in audience`,
                pickLeads: "Pick leads",
                sequenceIntro: "Build a multi-step sequence. Step 1 is sent immediately when you launch, next steps follow after their day delay.",
                addStep: "Add step",
                removeStep: "Remove step",
                dayOffset: (n) => n === 0 ? "Sent on launch" : `+${n} days after previous`,
                stepDayLabel: "Days after previous",
                stepSubject: "Subject",
                stepBody: "Body",
                stepVariant: "Variant",
                generateAI: "Generate with AI · 1c",
                generating: "Writing…",
                pickLeadForAI: "Pick a lead to personalize the first email",
                reviewTitle: "Ready to send",
                reviewAudience: "Audience",
                reviewSequence: "Sequence",
                reviewValue: "Value prop",
                launch: "Launch campaign",
                launching: "Launching…",
                saveDraft: "Save as draft",
                needName: "Campaign name is required",
                needSender: "Sender name is required",
                needAudience: "Add at least one lead to the audience",
                needStep: "Add at least one sequence step",
                needStepContent: "Each step needs a subject and body",
            },
            detail: {
                back: "Back to campaigns",
                overview: "Overview",
                sequence: "Sequence",
                events: "Recent events",
                launch: "Launch",
                pause: "Pause",
                resume: "Resume",
                delete: "Delete campaign",
                stats: { sent: "Sent", open: "Opens", click: "Clicks", reply: "Replies", bounce: "Bounces" },
                rates: { open: "Open rate", click: "Click rate", reply: "Reply rate", bounce: "Bounce rate" },
                empty: "No events yet — launch the campaign to see delivery + engagement.",
            },
            templates: {
                title: "Templates",
                subtitle: "Save reusable email templates. Use them as starting points in your sequences.",
                create: "New template",
                empty: "No templates yet",
                emptyBody: "Save your best-performing emails to reuse them across campaigns.",
                name: "Template name",
                subject: "Subject",
                body: "Body",
                category: "Category",
                categories: { general: "General", intro: "Intro", followup: "Follow-up", breakup: "Breakup", meeting: "Meeting" },
                save: "Save template",
                cancel: "Cancel",
                delete: "Delete",
            },
            inbox: {
                title: "Inbox",
                subtitle: "Replies and engagement events across your campaigns.",
                filters: { reply: "Replies", open: "Opens", click: "Clicks", sent: "Sent", bounce: "Bounces" },
                empty: "No events yet in this filter",
                columns: { lead: "Lead", subject: "Subject", type: "Type", when: "When" },
            },
        },
    },
    fr: {
        engage: {
            tabs: { campaigns: "Campagnes", templates: "Templates", inbox: "Inbox" },
            campaigns: {
                title: "Campagnes",
                subtitle: "Séquences multi-steps, emails générés par IA, A/B testing et inbox unifiée.",
                empty: "Aucune campagne",
                emptyBody: "Crée ta première séquence outbound — GPT-5.2 rédige les emails pour toi.",
                create: "Nouvelle campagne",
                statusLabels: { draft: "Brouillon", active: "Active", paused: "En pause", completed: "Terminée" },
                cols: { name: "Campagne", leads: "Leads", sent: "Envoyés", open: "Ouverture %", reply: "Réponse %", status: "Statut", updated: "MAJ" },
                pause: "Pause",
                resume: "Reprendre",
                delete: "Supprimer",
                view: "Voir",
                sending: "Envoi…",
                sentBanner: (n, sim) => sim ? `${n} emails programmés (mode simulation — ajoute RESEND_API_KEY pour envoyer en vrai)` : `${n} emails envoyés via Resend`,
            },
            wizard: {
                title: "Créer une campagne",
                steps: { audience: "Audience", sequence: "Séquence", review: "Revue & envoi" },
                back: "Retour",
                next: "Suivant",
                cancel: "Annuler",
                nameLabel: "Nom de campagne",
                namePh: "ex. Fintech EU T3 outreach",
                senderLabel: "Nom expéditeur (apparaît dans l'email)",
                senderPh: "ex. Alex de BOXEB2B",
                valueLabel: "Value prop (une phrase, utilisée par l'IA)",
                valuePh: "ex. Alternative self-serve aux agences lead gen à 40k€ — 50 crédits offerts.",
                audienceSource: "Source d'audience",
                sourceSegment: "Depuis un segment sauvegardé",
                sourceCurrent: "Depuis les filtres actuels",
                sourceManual: "Choisir des leads manuellement",
                selectSegment: "Choisir un segment",
                noSegments: "Aucun segment — crée-en un dans FIND → Segments.",
                leadsSelected: (n) => `${n} leads dans l'audience`,
                pickLeads: "Choisir des leads",
                sequenceIntro: "Construis une séquence multi-steps. Le step 1 part au lancement, les suivants après leur délai en jours.",
                addStep: "Ajouter un step",
                removeStep: "Supprimer",
                dayOffset: (n) => n === 0 ? "Envoyé au lancement" : `+${n} jours après le précédent`,
                stepDayLabel: "Jours après le précédent",
                stepSubject: "Sujet",
                stepBody: "Corps",
                stepVariant: "Variante",
                generateAI: "Générer avec l'IA · 1c",
                generating: "Rédaction…",
                pickLeadForAI: "Choisis un lead pour personnaliser le premier email",
                reviewTitle: "Prêt à envoyer",
                reviewAudience: "Audience",
                reviewSequence: "Séquence",
                reviewValue: "Value prop",
                launch: "Lancer la campagne",
                launching: "Lancement…",
                saveDraft: "Sauver en brouillon",
                needName: "Le nom de campagne est requis",
                needSender: "Le nom expéditeur est requis",
                needAudience: "Ajoute au moins un lead à l'audience",
                needStep: "Ajoute au moins un step",
                needStepContent: "Chaque step doit avoir un sujet et un corps",
            },
            detail: {
                back: "Retour aux campagnes",
                overview: "Vue d'ensemble",
                sequence: "Séquence",
                events: "Événements récents",
                launch: "Lancer",
                pause: "Mettre en pause",
                resume: "Reprendre",
                delete: "Supprimer",
                stats: { sent: "Envoyés", open: "Ouvertures", click: "Clics", reply: "Réponses", bounce: "Rebonds" },
                rates: { open: "Taux d'ouverture", click: "Taux de clic", reply: "Taux de réponse", bounce: "Taux de rebond" },
                empty: "Aucun événement — lance la campagne pour voir la délivrabilité.",
            },
            templates: {
                title: "Templates",
                subtitle: "Sauve tes emails réutilisables. Utilise-les comme point de départ dans tes séquences.",
                create: "Nouveau template",
                empty: "Aucun template",
                emptyBody: "Sauve tes meilleurs emails pour les réutiliser dans tes campagnes.",
                name: "Nom du template",
                subject: "Sujet",
                body: "Corps",
                category: "Catégorie",
                categories: { general: "Général", intro: "Intro", followup: "Relance", breakup: "Breakup", meeting: "RDV" },
                save: "Sauver",
                cancel: "Annuler",
                delete: "Supprimer",
            },
            inbox: {
                title: "Inbox",
                subtitle: "Réponses et événements d'engagement de toutes tes campagnes.",
                filters: { reply: "Réponses", open: "Ouvertures", click: "Clics", sent: "Envoyés", bounce: "Rebonds" },
                empty: "Aucun événement dans ce filtre",
                columns: { lead: "Lead", subject: "Sujet", type: "Type", when: "Quand" },
            },
        },
    },
};
